// Plain-English content for the Parent Dashboard: how to describe the quadrant and
// mindset, the conversation-starter question for each financial framework, and the
// logic for which frameworks showed up in this week's play.

import type { Quadrant, Txn } from '@biz-kid/engine';
import type { Framework } from '../builder/weeklyDecisionDeck.js';

export const QUADRANT_INFO: Record<Quadrant, { name: string; plain: string }> = {
  E: { name: 'Employee', plain: 'earns by working for money — the starting point everyone begins from.' },
  S: { name: 'Self-employed', plain: 'owns a small hustle — earns when they show up and do the work.' },
  B: { name: 'Business owner', plain: 'owns something that earns even when they step away.' },
  I: { name: 'Investor', plain: 'has money working for them — the goal of the whole journey.' },
};

export const MINDSET_LABEL: Record<string, string> = {
  desire: 'Desire',
  faith: 'Self-belief',
  persistence: 'Persistence',
  focus: 'Focus',
  gratitude: 'Gratitude',
};

export const FRAMEWORK_ORDER: Framework[] = ['Cashflow', 'Compounding', 'Risk', 'Leverage', 'Reputation'];

export const FRAMEWORK_INFO: Record<Framework, { blurb: string; question: string }> = {
  Cashflow: {
    blurb: 'money in vs money out; assets vs liabilities',
    question: 'What’s the difference between something that puts money in your pocket and something that takes money out?',
  },
  Compounding: {
    blurb: 'reinvesting and patient growth',
    question: 'Why might it be smarter to reinvest a profit and let it grow than to spend it right away?',
  },
  Risk: {
    blurb: 'volatility, losses, and not panicking',
    question: 'If something you owned suddenly dropped in value, what would you do — and why?',
  },
  Leverage: {
    blurb: 'hiring, borrowing, and systems to grow',
    question: 'When do you think it’s worth hiring help or borrowing money to grow something?',
  },
  Reputation: {
    blurb: 'trust, fairness, giving back',
    question: 'Why does being trusted and fair matter so much when you run a business?',
  },
};

/** Cashflow for a settled week (active + passive − expense − debt). */
export function weekCashflow(ledger: Txn[], w: number): number {
  return ledger.filter((t) => t.week === w).reduce((s, t) => (t.category === 'capital' ? s : s + t.amount), 0);
}

export type LessonSignals = {
  thisWeekCardFramework: Framework;
  recentConsequenceFrameworks: string[]; // pending + landed
  ranBusinessThisWeek: boolean;
  ownsIncomeAsset: boolean;
  hasLeverage: boolean; // loan or investor
  hadSetback: boolean; // in a slump / loss
  reputationMoved: boolean;
};

/** Which of the five frameworks have shown up in play this week. */
export function lessonsThisWeek(s: LessonSignals): Record<Framework, boolean> {
  const seen = new Set<Framework>();
  seen.add(s.thisWeekCardFramework);
  for (const f of s.recentConsequenceFrameworks) if (isFramework(f)) seen.add(f);
  if (s.ranBusinessThisWeek) seen.add('Cashflow');
  if (s.ownsIncomeAsset) seen.add('Compounding');
  if (s.hasLeverage) seen.add('Leverage');
  if (s.hadSetback) seen.add('Risk');
  if (s.reputationMoved) seen.add('Reputation');
  return {
    Cashflow: seen.has('Cashflow'),
    Compounding: seen.has('Compounding'),
    Risk: seen.has('Risk'),
    Leverage: seen.has('Leverage'),
    Reputation: seen.has('Reputation'),
  };
}

function isFramework(x: string): x is Framework {
  return FRAMEWORK_ORDER.includes(x as Framework);
}
